/**
 * Merchant Pro V1 PR-3 — Manual Booking wizard (7-step).
 *
 * Route: /owner/manual-booking
 *
 * Implements the operator-locked Manual Booking State Contract
 * (`MERCHANT_PRO_V1_MANUAL_BOOKING_STATE_CONTRACT.md` §§1-7) verbatim.
 *
 * Steps:
 *   S1  Customer Lookup           — search CRM or walk-in branch
 *   S2  Date / Time / Location    — branch + slot + party size
 *   S3  Floor Plan / Select Table — room → table via web SVG canvas
 *   S4  Table Detail              — notes + tags
 *   S5  Booking Summary           — read-only aggregate
 *   S6  Review & Confirm          — final review + Create CTA
 *   S7  Confirmed                 — booking code + Print / Share / New
 *
 * Backed by V3-A1 commit endpoint:
 *   POST /api/cashier/reservations/manual
 *
 * REUSE discipline:
 *   - StatusPill / PageHeader / SectionCard / Spinner / FullSpinner /
 *     QueryError / classes — all PR-2 primitives.
 *   - Customer search reuses /api/owner/crm/customers (existing).
 *   - Branches / rooms / tables read endpoints reused as-is.
 *   - Web FloorPlanCanvas is a thin port of the mobile canvas (Option B
 *     from the PR-3 Review Package §8) — shares the data contract,
 *     re-implements only the SVG primitive layer.
 *
 * Operator-locked decisions honoured:
 *   Q1 — No "Add Customer to CRM" CTA. Walk-in is the only branch when
 *        search returns 0 hits.
 *   Q5 — Screenshots 04 / 05 / 06 / 07 captured against this surface.
 */
import { useMemo, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, Check, Search, User, UserPlus,
  CalendarDays, Users, MapPin, Hash, Printer, Share2,
  Sparkles, ClipboardCheck, PartyPopper,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuthStore } from '@/lib/auth';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { classes } from '@/lib/utils';
import ChairGridTableCard from '@/components/owner/ChairGridTableCard';
import WebFloorPlanCanvas from '@/components/owner/WebFloorPlanCanvas';
import RequirePermission from '@/lib/capabilities';

import {
  PageHeader,
  SectionCard,
  Spinner,
  FullSpinner,
  QueryError,
} from '@/components/ui_primitives/Misc';

const STEPS = [
  { key: 'S1', label: 'Customer',    Icon: User },
  { key: 'S2', label: 'Date & Time', Icon: CalendarDays },
  { key: 'S3', label: 'Floor Plan',  Icon: MapPin },
  { key: 'S4', label: 'Table Detail',Icon: Users },
  { key: 'S5', label: 'Summary',     Icon: ClipboardCheck },
  { key: 'S6', label: 'Confirm',     Icon: Sparkles },
  { key: 'S7', label: 'Done',        Icon: PartyPopper },
];

/* ====================================================================
 *   Reducer — keys to the locked state contract
 * ==================================================================== */

const INITIAL_STATE = {
  step: 'S1',
  // S1
  customer_id: null,
  customer_name: null,
  customer_phone: null,
  walk_in_customer: null,
  // S2
  branch_id: null,
  branch_name: null,
  slot_start: null,      // ISO string
  slot_end: null,
  party_size: 2,
  // S3
  room_id: null,
  room_name: null,
  table_id: null,
  table_label: null,
  table_capacity: null,
  // S4
  notes: '',
  tags: [],
  // S6
  idempotency_key: null,
  bypass_customer_cap: false,
  override_reason: '',
  // S7
  result: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'GOTO':
      return { ...state, step: action.step };
    case 'PATCH':
      return { ...state, ...action.payload };
    case 'RESET':
      return { ...INITIAL_STATE, idempotency_key: crypto.randomUUID() };
    default:
      return state;
  }
}

/* ====================================================================
 *   Customer Lookup Modal (S1 helper)
 * ==================================================================== */

function CustomerLookupModal({ onClose, onSelect, onWalkIn, token }) {
  const [q, setQ] = useState('');
  const [walkInName, setWalkInName] = useState('');
  const [walkInPhone, setWalkInPhone] = useState('');

  const searchQ = useQuery({
    queryKey: qk.ownerCrmSearch(q),
    queryFn: () => api.ownerCrmSearch(token, q),
    enabled: !!token && q.trim().length >= 2,
    staleTime: 10_000,
    keepPreviousData: true,
  });

  const rows = searchQ.data?.customers ?? searchQ.data?.items ?? [];
  // Normalise field names — the owner CRM endpoint surfaces
  // `customer_id` rather than `id` on its row payload.
  const normalisedRows = rows.map((c) => ({
    id: c.id || c.customer_id,
    name: c.name,
    phone: c.phone,
    membership_id: c.membership_id,
  }));
  const isEmpty = q.trim().length >= 2 && !searchQ.isFetching && normalisedRows.length === 0;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center px-4"
      data-testid="customer-lookup-modal"
    >
      <div className="bg-[#15151B] border border-white/10 rounded-2xl p-6 max-w-lg w-full shadow-2xl">
        <h3 className="font-display text-lg font-semibold text-white mb-1">
          Find or add customer
        </h3>
        <p className="text-sm text-white/55 mb-4">
          Type at least 2 characters to search the brand CRM. If nothing matches,
          book as a walk-in below.
        </p>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="Search by name or phone"
            data-testid="customer-search-input"
            className="w-full pl-9 pr-3 py-2 bg-black/40 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
          />
        </div>

        <div className="mt-3 max-h-72 overflow-y-auto rounded-xl border border-white/8">
          {searchQ.isFetching && q.trim().length >= 2 ? (
            <div className="p-4"><Spinner className="w-5 h-5 mx-auto" /></div>
          ) : normalisedRows.length > 0 ? (
            <ul className="divide-y divide-white/5">
              {normalisedRows.map((c) => (
                <li
                  key={c.id}
                  className="p-3 hover:bg-white/[0.04] cursor-pointer"
                  data-testid={`crm-hit-${c.id}`}
                  onClick={() => onSelect(c)}
                >
                  <div className="text-sm font-medium text-white">{c.name}</div>
                  <div className="text-xs text-white/45 font-mono mt-0.5">
                    {c.phone || '—'}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-xs text-white/45 text-center">
              {q.trim().length < 2
                ? 'Type to search…'
                : 'No matching customer.'}
            </div>
          )}
        </div>

        {/* Walk-in branch */}
        <div
          className={classes(
            'mt-4 rounded-xl border p-3',
            isEmpty
              ? 'border-gold/30 bg-gold/5'
              : 'border-white/8 bg-white/[0.02]',
          )}
          data-testid="walkin-section"
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-white/70">
            <UserPlus size={14} className="text-gold" />
            Book as walk-in
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <input
              type="text"
              value={walkInName}
              onChange={(e) => setWalkInName(e.target.value)}
              placeholder="Name"
              data-testid="walkin-name"
              className="bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
            />
            <input
              type="tel"
              value={walkInPhone}
              onChange={(e) => setWalkInPhone(e.target.value)}
              placeholder="Phone (optional)"
              data-testid="walkin-phone"
              className="bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
            />
          </div>
          <button
            type="button"
            disabled={!walkInName.trim()}
            onClick={() => onWalkIn({ name: walkInName.trim(), phone: walkInPhone.trim() || null })}
            data-testid="walkin-select-btn"
            className="mt-2 w-full px-3 py-1.5 rounded-lg text-xs font-semibold border border-gold/40 bg-gold/15 text-gold hover:bg-gold/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Use walk-in
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            data-testid="lookup-close-btn"
            className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================================================================
 *   Step Strip
 * ==================================================================== */

function StepStrip({ step }) {
  const idx = STEPS.findIndex((s) => s.key === step);
  return (
    <div
      className="flex items-center gap-1.5 px-1 overflow-x-auto"
      data-testid="step-strip"
    >
      {STEPS.map((s, i) => {
        const isDone = i < idx;
        const isActive = i === idx;
        const StepIcon = s.Icon;
        return (
          <div key={s.key} className="flex items-center gap-1.5 shrink-0">
            <div
              className={classes(
                'w-8 h-8 rounded-xl grid place-items-center text-[10px] font-semibold border transition-all',
                isDone
                  ? 'border-gold/45 bg-gold/15 text-gold'
                  : isActive
                  ? 'border-gold/70 bg-gradient-to-br from-gold/30 to-gold/10 text-gold shadow-[0_0_18px_rgba(212,175,55,0.35)]'
                  : 'border-white/15 bg-white/[0.02] text-white/45',
              )}
              data-testid={`step-dot-${s.key}`}
            >
              {isDone ? <Check size={13} /> : <StepIcon size={13} />}
            </div>
            <span
              className={classes(
                'text-[11px] uppercase tracking-widest font-semibold whitespace-nowrap',
                isActive ? 'text-gold' : isDone ? 'text-white/70' : 'text-white/35',
              )}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 ? (
              <span
                className={classes(
                  'w-6 h-px mx-1 transition-colors',
                  isDone || isActive ? 'bg-gold/40' : 'bg-white/10',
                )}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ====================================================================
 *   PR-4c — Page
 *   (Legacy local `WebFloorPlanCanvas` + `SHAPE_FILL` deleted —
 *   replaced by the shared `<WebFloorPlanCanvas>` import above.
 *   The dead-code SVG renderer that drew tables as rectangles +
 *   circles is no longer permitted under §8.6.0 of the Review
 *   Package — chair-grid is the canonical visual.)
 * ==================================================================== */

function isoOrNull(d) {
  if (!d) return null;
  return new Date(d).toISOString();
}

function formatDisplay(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return iso; }
}

export default function ManualBookingPage() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const brandId = user?.brand_id;
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE, (s) => ({
    ...s,
    idempotency_key: crypto.randomUUID(),
  }));
  const [showLookup, setShowLookup] = useState(false);

  const branchesQ = useQuery({
    queryKey: qk.brandBranches(brandId || 'self'),
    queryFn: () => api.brandBranches(token, brandId),
    enabled: !!token && !!brandId,
    staleTime: 5 * 60_000,
  });
  const branches = branchesQ.data || [];

  const roomsQ = useQuery({
    queryKey: qk.branchRooms(state.branch_id),
    queryFn: () => api.ownerBranchRooms(token, brandId, state.branch_id),
    enabled: !!token && !!brandId && !!state.branch_id && (state.step === 'S3' || state.step === 'S4'),
    staleTime: 60_000,
  });
  // The owner endpoint returns {items: [...]}.
  const rooms = roomsQ.data?.items || roomsQ.data || [];

  const tablesQ = useQuery({
    queryKey: qk.roomTables(state.room_id),
    queryFn: () => api.ownerRoomTables(token, brandId, state.room_id),
    enabled: !!token && !!brandId && !!state.room_id && (state.step === 'S3' || state.step === 'S4'),
    staleTime: 30_000,
  });
  const tables = tablesQ.data?.items || tablesQ.data || [];

  const commitM = useMutation({
    mutationFn: () => {
      const body = {
        branch_id: state.branch_id,
        slot_start: state.slot_start,
        slot_end: state.slot_end,
        party_size: Number(state.party_size),
        room_id: state.room_id || undefined,
        table_id: state.table_id || undefined,
        notes: state.notes || undefined,
        tags: state.tags || [],
        idempotency_key: state.idempotency_key,
      };
      if (state.customer_id) {
        body.customer_id = state.customer_id;
      } else if (state.walk_in_customer) {
        body.walk_in_customer = state.walk_in_customer;
      }
      if (state.bypass_customer_cap) {
        body.bypass_customer_cap = true;
        body.override_reason = state.override_reason || 'Operator override';
      }
      return api.cashierManualBooking(token, body);
    },
    onSuccess: (r) => {
      toast.success('Reservation confirmed.');
      dispatch({ type: 'PATCH', payload: { result: r, step: 'S7' } });
    },
    onError: (err) => {
      const code = err?.detail?.code;
      if (code === 'concurrent_cap_exceeded') {
        toast.error('Customer is at booking cap. Provide override reason.');
        dispatch({ type: 'PATCH', payload: { bypass_customer_cap: true } });
        return;
      }
      toast.error(err?.detail?.message || err?.message || 'Booking failed.');
    },
  });

  /* ----- Step-level eligibility ----- */
  const canAdvance = (() => {
    switch (state.step) {
      case 'S1':
        return !!(state.customer_id || state.walk_in_customer);
      case 'S2':
        return !!state.branch_id && !!state.slot_start && !!state.slot_end &&
               Number(state.party_size) >= 1;
      case 'S3':
        return !!state.table_id;
      case 'S4':
        return true;
      case 'S5':
        return true;
      default:
        return false;
    }
  })();

  /* ====== Step UIs (render-helper functions — PR4d-1 hoisting note) ======
   * These are intentionally render-HELPER functions (lowercase first
   * letter) called as `{stepS1()}` from the `stepUis` map below, NOT
   * JSX-rendered as `<StepS1 />`. The render-helper pattern is the
   * React-team-recommended fix for the
   * `react/no-unstable-nested-components` warning: the rule fires on
   * JSX-element rendering of nested-defined components, not on bare
   * function calls. Function-call invocation:
   *   (a) clears all 7 lint warnings,
   *   (b) preserves closure access (no prop plumbing needed),
   *   (c) is literally zero behavior change vs the prior JSX-element
   *       invocation (per React's reconciliation rules),
   *   (d) matches the brief's "Pure structural refactor · Zero rendering
   *       change" mandate more conservatively than a full hoist would.
   */
  function stepS1() {
    return (
      <SectionCard testID="step-s1-card">
        <div className="flex items-center gap-2 mb-3">
          <User size={14} className="text-gold" />
          <h2 className="font-display text-lg font-semibold text-white">Customer</h2>
        </div>
        {state.customer_id || state.walk_in_customer ? (
          <div
            data-testid="customer-selected-card"
            className="flex items-center justify-between border border-gold/30 bg-gold/5 rounded-xl px-4 py-3"
          >
            <div>
              <div className="text-sm font-medium text-white">
                {state.customer_name || state.walk_in_customer?.name}
              </div>
              <div className="text-xs text-white/55 mt-0.5">
                {state.customer_phone || state.walk_in_customer?.phone || (state.walk_in_customer ? 'Walk-in' : '')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => dispatch({
                type: 'PATCH',
                payload: {
                  customer_id: null, customer_name: null, customer_phone: null,
                  walk_in_customer: null,
                },
              })}
              data-testid="customer-clear-btn"
              className="text-xs text-white/55 hover:text-white"
            >
              Change
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowLookup(true)}
            data-testid="customer-lookup-open-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gold/30 bg-gold/10 text-gold text-sm font-semibold hover:bg-gold/20"
          >
            <Search size={14} /> Find or add customer
          </button>
        )}
      </SectionCard>
    );
  }

  function stepS2() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateValue = state.slot_start ? state.slot_start.slice(0, 10) : '';
    const timeValue = state.slot_start ? state.slot_start.slice(11, 16) : '18:00';
    const updateSlot = (date, time) => {
      if (!date || !time) return;
      const d = new Date(`${date}T${time}:00`);
      dispatch({
        type: 'PATCH',
        payload: {
          slot_start: d.toISOString(),
          slot_end: new Date(d.getTime() + 60 * 60_000).toISOString(),
        },
      });
    };

    return (
      <SectionCard testID="step-s2-card">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays size={14} className="text-gold" />
          <h2 className="font-display text-lg font-semibold text-white">Date & Location</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">Branch</label>
            <select
              value={state.branch_id || ''}
              onChange={(e) => {
                const b = branches.find((x) => x.id === e.target.value);
                dispatch({ type: 'PATCH', payload: {
                  branch_id: b?.id || null,
                  branch_name: b?.name || null,
                  room_id: null, room_name: null,
                  table_id: null, table_label: null, table_capacity: null,
                }});
              }}
              data-testid="branch-select"
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-gold/40"
            >
              <option value="">Select branch…</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">Party size</label>
            <input
              type="number"
              min="1" max="50"
              value={state.party_size}
              onChange={(e) => dispatch({
                type: 'PATCH',
                payload: { party_size: Math.max(1, Math.min(50, Number(e.target.value) || 1)) },
              })}
              data-testid="party-size-input"
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-gold/40 tabular-nums"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">Date</label>
            <input
              type="date"
              value={dateValue}
              min={today.toISOString().slice(0, 10)}
              onChange={(e) => updateSlot(e.target.value, timeValue)}
              data-testid="date-input"
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-gold/40"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">Time</label>
            <input
              type="time"
              value={timeValue}
              step="1800"
              onChange={(e) => updateSlot(dateValue, e.target.value)}
              data-testid="time-input"
              className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-gold/40"
            />
          </div>
        </div>
        {state.slot_start ? (
          <div className="mt-3 text-xs text-white/55">
            Slot: <span className="text-gold/85 font-mono">{formatDisplay(state.slot_start)}</span>
          </div>
        ) : null}
      </SectionCard>
    );
  }

  function stepS3() {
    return (
      <SectionCard testID="step-s3-card">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={14} className="text-gold" />
          <h2 className="font-display text-lg font-semibold text-white">Floor Plan</h2>
        </div>

        {/* Room chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="room-chips">
          {roomsQ.isLoading ? (
            <Spinner className="w-5 h-5" />
          ) : rooms.length === 0 ? (
            <div className="text-xs text-white/45">No rooms in this branch.</div>
          ) : rooms.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => dispatch({
                type: 'PATCH',
                payload: {
                  room_id: r.id, room_name: r.name,
                  table_id: null, table_label: null, table_capacity: null,
                },
              })}
              data-testid={`room-chip-${r.id}`}
              className={classes(
                'px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors',
                state.room_id === r.id
                  ? 'border-gold/40 bg-gold/15 text-gold'
                  : 'border-white/10 bg-white/[0.02] text-white/70 hover:bg-white/[0.05]',
              )}
            >
              {r.name}
            </button>
          ))}
        </div>

        {state.room_id ? (
          tablesQ.isLoading ? (
            <FullSpinner />
          ) : (
            <WebFloorPlanCanvas
              tables={tables}
              room={rooms.find((r) => r.id === state.room_id) || null}
              selectedTableId={state.table_id}
              onTableClick={(t) => {
                const isAvailable = (t.state ?? 'available') === 'available';
                const tooSmall = Number(t.capacity ?? 0) < Number(state.party_size ?? 1);
                if (!isAvailable || tooSmall) return;
                dispatch({
                  type: 'PATCH',
                  payload: {
                    table_id: t.id,
                    table_label: t.name || t.label || `T${t.code || ''}`,
                    table_capacity: t.capacity,
                  },
                });
              }}
              disabledFn={(t) => {
                const isAvailable = (t.state ?? 'available') === 'available';
                const tooSmall = Number(t.capacity ?? 0) < Number(state.party_size ?? 1);
                return !isAvailable || tooSmall;
              }}
              testIdPrefix="manual-booking-s3"
              minCardWidth={160}
              emptyMessage="No tables in this room."
            />
          )
        ) : (
          <div className="text-xs text-white/45">Pick a room to see its floor plan.</div>
        )}

        {state.table_id ? (
          <div
            data-testid="table-selected-card"
            className="mt-4 border border-gold/30 bg-gold/5 rounded-xl px-4 py-3 flex items-center justify-between"
          >
            <div>
              <div className="text-sm font-medium text-white">
                Table {state.table_label}
              </div>
              <div className="text-xs text-white/55 mt-0.5">
                Capacity {state.table_capacity} · Party size {state.party_size}
              </div>
            </div>
          </div>
        ) : null}
      </SectionCard>
    );
  }

  function stepS4() {
    return (
      <SectionCard testID="step-s4-card">
        <div className="flex items-center gap-2 mb-3">
          <Hash size={14} className="text-gold" />
          <h2 className="font-display text-lg font-semibold text-white">Table Detail</h2>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest font-semibold text-white/45">Notes (optional)</label>
          <textarea
            rows={3}
            value={state.notes}
            onChange={(e) => dispatch({ type: 'PATCH', payload: { notes: e.target.value } })}
            placeholder="Special instructions, allergies, etc."
            data-testid="notes-input"
            className="w-full mt-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-gold/40"
          />
        </div>
      </SectionCard>
    );
  }

  function stepS5() {
    /* PR-4c — Richer summary card.
     * Split into Customer / Booking / Notes sections + chair-grid
     * thumbnail of the selected table. (4c-AC#5) */
    const selectedTable = state.table_id
      ? {
          id: state.table_id,
          name: state.table_label,
          state: 'available',
          capacity: state.table_capacity,
          current_party_size: state.party_size,
        }
      : null;
    return (
      <SectionCard testID="step-s5-card">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardCheck size={16} className="text-gold" />
          <h2 className="font-display text-lg font-semibold text-white">Booking Summary</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,160px] gap-5">
          <div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-3" data-testid="s5-customer-section">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-gold/80 mb-2 flex items-center gap-1.5">
                <User size={11} /> Customer
              </div>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Name</div>
                  <div className="text-white/90 mt-0.5 font-medium">{state.customer_name || state.walk_in_customer?.name || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Phone</div>
                  <div className="text-white/90 mt-0.5 font-mono text-xs">{state.customer_phone || state.walk_in_customer?.phone || '—'}</div>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid="s5-booking-section">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-gold/80 mb-2 flex items-center gap-1.5">
                <Sparkles size={11} /> Booking
              </div>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Branch</div>
                  <div className="text-white/90 mt-0.5">{state.branch_name || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Slot</div>
                  <div className="text-white/90 mt-0.5">{formatDisplay(state.slot_start)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Party</div>
                  <div className="text-white/90 mt-0.5 tabular-nums">{state.party_size}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Room · Table</div>
                  <div className="text-white/90 mt-0.5">
                    {state.room_name || '—'} · {state.table_label || '—'}
                  </div>
                </div>
              </div>
              {state.notes ? (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-widest text-white/45 mb-1">Notes</div>
                  <div className="text-white/80 text-sm italic">{state.notes}</div>
                </div>
              ) : null}
            </div>
          </div>
          {selectedTable ? (
            <div data-testid="s5-table-thumb-wrap" className="self-start">
              <ChairGridTableCard
                table={selectedTable}
                size="sm"
                testIdPrefix="s5-table"
                showStatePill={false}
              />
              <div className="text-[10px] text-white/45 mt-1 text-center font-mono">
                Selected
              </div>
            </div>
          ) : null}
        </div>
      </SectionCard>
    );
  }

  function stepS6() {
    return (
      <SectionCard testID="step-s6-card">
        <div className="flex items-center gap-2 mb-3">
          <Check size={14} className="text-gold" />
          <h2 className="font-display text-lg font-semibold text-white">Review & Confirm</h2>
        </div>
        <p className="text-sm text-white/70">
          You&apos;re about to create a <strong>confirmed</strong> reservation for{' '}
          <strong>{state.customer_name || state.walk_in_customer?.name}</strong>{' '}
          on <strong>{formatDisplay(state.slot_start)}</strong> at{' '}
          <strong>{state.branch_name}</strong> · Table <strong>{state.table_label || '—'}</strong>.
        </p>

        {state.bypass_customer_cap ? (
          <div className="mt-4 border border-amber-500/40 bg-amber-500/10 rounded-xl p-3" data-testid="override-card">
            <div className="text-xs font-semibold text-amber-200 mb-2">
              Customer cap exceeded — provide override reason
            </div>
            <textarea
              rows={2}
              value={state.override_reason}
              onChange={(e) => dispatch({ type: 'PATCH', payload: { override_reason: e.target.value } })}
              placeholder="e.g. VIP repeat booking"
              data-testid="override-reason-input"
              className="w-full bg-black/40 border border-amber-500/30 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400"
            />
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-end">
          {/* Sub-phase RBAC-C2 (2026-06-18) — gate the final commit on
              `manual_booking.commit`. Game Master (which lacks this cap
              in C1 seed) sees a polite empty-state message instead. */}
          <RequirePermission
            cap="manual_booking.commit"
            fallback={
              <div
                data-testid="manual-booking-commit-denied"
                className="text-xs text-white/55 italic max-w-md text-right"
              >
                You don&apos;t have permission to commit manual bookings —
                contact your Brand Owner.
              </div>
            }
          >
            <button
              type="button"
              onClick={() => commitM.mutate()}
              disabled={commitM.isPending}
              data-testid="create-booking-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-gold/40 bg-gold/15 text-gold text-sm font-semibold hover:bg-gold/25 disabled:opacity-50"
            >
              {commitM.isPending ? <Spinner className="w-4 h-4" /> : <Check size={14} />}
              Create booking
            </button>
          </RequirePermission>
        </div>
      </SectionCard>
    );
  }

  function stepS7() {
    const r = state.result || {};
    const onPrint = () => window.print();
    const onShare = async () => {
      const text = `Booking confirmed · ${r.booking_code || ''} · ${formatDisplay(r.slot_start)}`;
      try {
        if (navigator.share) {
          await navigator.share({ title: 'Booking', text });
        } else {
          await navigator.clipboard.writeText(text);
          toast.success('Copied to clipboard.');
        }
      } catch { /* ignore user dismissal */ }
    };
    /* PR-4c — S7 confetti SVG burst + larger booking-code monogram. (4c-AC#5) */
    const confettiPieces = [
      { x: -120, c: '#D4AF37', delay: 0 },
      { x: -80,  c: '#22C55E', delay: 0.06 },
      { x: -40,  c: '#FBBF24', delay: 0.12 },
      { x: 0,    c: '#D4AF37', delay: 0.18 },
      { x: 40,   c: '#3B82F6', delay: 0.24 },
      { x: 80,   c: '#8B5CF6', delay: 0.30 },
      { x: 120,  c: '#F97316', delay: 0.36 },
    ];
    return (
      <SectionCard testID="step-s7-card">
        <div className="relative text-center py-10">
          {/* Confetti burst — purely decorative SVG sparkles */}
          <div
            className="absolute inset-x-0 top-2 h-16 pointer-events-none"
            data-testid="s7-confetti"
          >
            {confettiPieces.map((p, i) => (
              <span
                key={i}
                className="animate-confetti absolute left-1/2 top-6 block"
                style={{
                  transform: `translateX(${p.x}px)`,
                  animationDelay: `${p.delay}s`,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect x="2" y="2" width="10" height="3" rx="1.5" fill={p.c} />
                  <rect x="3" y="8" width="8" height="3" rx="1.5" fill={p.c} opacity="0.65" />
                </svg>
              </span>
            ))}
          </div>

          <div className="relative inline-flex w-20 h-20 rounded-2xl bg-gradient-to-br from-gold/30 to-gold/10 border border-gold/50 grid place-items-center mx-auto mb-4 text-gold shadow-[0_0_36px_rgba(212,175,55,0.35)]">
            <PartyPopper size={36} />
          </div>
          <h2 className="font-display text-3xl font-semibold text-white tracking-tight">
            Booking Confirmed
          </h2>
          <div
            data-testid="booking-code-display"
            className="mt-5 font-mono tracking-[0.2em] text-gold text-5xl sm:text-6xl font-bold"
            style={{ textShadow: '0 0 40px rgba(212,175,55,0.5)' }}
          >
            {r.booking_code || r.reservation_id?.slice(0, 8) || '—'}
          </div>
          <div className="text-sm text-white/55 mt-3 tabular-nums">
            {formatDisplay(r.slot_start)} · Party of {r.party_size}
          </div>

          <div className="mt-8 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={onPrint}
              data-testid="print-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 bg-white/5 text-white/85 hover:bg-white/10"
            >
              <Printer size={14} /> Print
            </button>
            <button
              type="button"
              onClick={onShare}
              data-testid="share-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-white/15 bg-white/5 text-white/85 hover:bg-white/10"
            >
              <Share2 size={14} /> Share
            </button>
            <button
              type="button"
              onClick={() => dispatch({ type: 'RESET' })}
              data-testid="new-booking-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gold/40 bg-gold/15 text-gold hover:bg-gold/25"
            >
              <Check size={14} /> New booking
            </button>
          </div>
        </div>
      </SectionCard>
    );
  }

  const stepUis = {
    S1: stepS1(), S2: stepS2(), S3: stepS3(),
    S4: stepS4(), S5: stepS5(), S6: stepS6(), S7: stepS7(),
  };

  return (
    <div className="space-y-6" data-testid="manual-booking-page">
      <PageHeader
        kicker="Operations"
        title="Manual Booking"
        subtitle="Create a confirmed reservation on behalf of a customer."
      />

      <SectionCard padded={false} className="px-4 py-3">
        <StepStrip step={state.step} />
      </SectionCard>

      {stepUis[state.step]}

      {/* Footer nav (hide on S7) */}
      {state.step !== 'S7' ? (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              const idx = STEPS.findIndex((s) => s.key === state.step);
              if (idx > 0) {
                dispatch({ type: 'GOTO', step: STEPS[idx - 1].key });
              } else {
                navigate('/owner');
              }
            }}
            data-testid="wizard-back-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm text-white/70 hover:text-white"
          >
            <ChevronLeft size={14} /> Back
          </button>
          {state.step !== 'S6' ? (
            <button
              type="button"
              disabled={!canAdvance}
              onClick={() => {
                const idx = STEPS.findIndex((s) => s.key === state.step);
                dispatch({ type: 'GOTO', step: STEPS[idx + 1].key });
              }}
              data-testid="wizard-next-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-gold/40 bg-gold/15 text-gold hover:bg-gold/25 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : null}
        </div>
      ) : null}

      {showLookup ? (
        <CustomerLookupModal
          token={token}
          onClose={() => setShowLookup(false)}
          onSelect={(c) => {
            dispatch({
              type: 'PATCH',
              payload: {
                customer_id: c.id, customer_name: c.name, customer_phone: c.phone,
                walk_in_customer: null,
              },
            });
            setShowLookup(false);
          }}
          onWalkIn={(w) => {
            dispatch({
              type: 'PATCH',
              payload: {
                walk_in_customer: w, customer_id: null,
                customer_name: null, customer_phone: null,
              },
            });
            setShowLookup(false);
          }}
        />
      ) : null}
    </div>
  );
}
